import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { FeatureStatus } from '@/features/patient-record/types';

const STATUS_ORDER: readonly FeatureStatus[] = ['present', 'absent', 'unknown'];

/**
 * Semantic colour per status — green = present, red = absent, neutral = unknown.
 * Applied as a soft-chip tint on the trigger so the row's status stays scannable,
 * and as the text colour on each menu option.
 */
const STATUS_STYLE: Readonly<Record<FeatureStatus, string>> = {
  present: 'bg-success/15 text-success',
  absent: 'bg-destructive/10 text-destructive',
  unknown: 'bg-muted text-foreground',
};

interface FeatureStatusToggleProps {
  readonly value: FeatureStatus;
  readonly onChange: (next: FeatureStatus) => void;
  readonly disabled?: boolean;
}

/**
 * Compact Present / Absent / Unknown status control — a single tinted pill that
 * opens a dropdown to change the value. The caret makes it obviously clickable
 * (the reason PTV2-1170 moved off a bare badge), while staying far narrower than
 * the old three-segment toggle.
 */
export function FeatureStatusToggle({
  value,
  onChange,
  disabled,
}: FeatureStatusToggleProps): ReactElement {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled === true}
          aria-label={t('patient.phenotypes.statusGroupLabel')}
          className={cn('h-7 gap-1 px-2 text-[11px] font-medium', STATUS_STYLE[value])}
        >
          {t(`patient.phenotypes.status.${value}`)}
          <ChevronDown className="h-3 w-3 opacity-60" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[9rem]">
        {STATUS_ORDER.map((option) => (
          <DropdownMenuItem
            key={option}
            onSelect={() => {
              if (option !== value) onChange(option);
            }}
          >
            <Check
              className={cn('h-3.5 w-3.5', option === value ? 'opacity-100' : 'opacity-0')}
              aria-hidden="true"
            />
            {t(`patient.phenotypes.status.${option}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
